// NOTE: imported from the deep path on purpose, the package index pulls in
// `@gig/magic-messaging` which cannot be transformed by jest.
import { Jurisdiction } from '@gig/magic-jurisdictions/dist/jurisdictions.constants';
import { getUserJurisdiction } from '../services/routing/helpers/jurisdiction.helpers';

/**
 * How a Time-out block has to be handled for the current user.
 */
export enum TimeOutHandling {
    /**
     * The jurisdiction is out of scope, the Time-out block is not handled
     * by the frontend at all.
     */
    NONE = 'NONE',
    /**
     * The user may log in, but gameplay and deposits are restricted and the
     * "Account in Time-out" modal is displayed.
     */
    RESTRICTED_ACCESS = 'RESTRICTED_ACCESS',
    /**
     * The user must not be able to log in while the Time-out lasts.
     */
    LOGIN_BLOCKED = 'LOGIN_BLOCKED',
}

/**
 * Jurisdictions where a timed out user is allowed to log in to withdraw,
 * update personal details and access responsible gaming resources.
 *
 * Extend this list to roll the feature out to further jurisdictions.
 */
export const TIME_OUT_RESTRICTED_ACCESS_JURISDICTIONS: Jurisdiction[] = [
    // Core regulations, the default jurisdiction of the Curaçao brands
    Jurisdiction.MGA,
    // Ontario
    Jurisdiction.AGCO,
    // Alberta
    Jurisdiction.AGLC,
];

/**
 * Jurisdictions where logging in is not permitted during a Time-out period.
 */
export const TIME_OUT_LOGIN_BLOCKED_JURISDICTIONS: Jurisdiction[] = [
    // Estonia
    Jurisdiction.EMTA,
];

export function isTimeOutRestrictedAccessJurisdiction(
    jurisdiction: Jurisdiction | null,
): boolean {
    return (
        !!jurisdiction &&
        TIME_OUT_RESTRICTED_ACCESS_JURISDICTIONS.includes(jurisdiction)
    );
}

export function isTimeOutLoginBlockedJurisdiction(
    jurisdiction: Jurisdiction | null,
): boolean {
    return (
        !!jurisdiction &&
        TIME_OUT_LOGIN_BLOCKED_JURISDICTIONS.includes(jurisdiction)
    );
}

export function getTimeOutHandlingForJurisdiction(
    jurisdiction: Jurisdiction | null,
): TimeOutHandling {
    if (isTimeOutLoginBlockedJurisdiction(jurisdiction)) {
        return TimeOutHandling.LOGIN_BLOCKED;
    }

    if (isTimeOutRestrictedAccessJurisdiction(jurisdiction)) {
        return TimeOutHandling.RESTRICTED_ACCESS;
    }

    return TimeOutHandling.NONE;
}

/**
 * Resolves how a Time-out block has to be handled for the jurisdiction of
 * the current user. Falls back to `NONE` when the jurisdiction cannot be
 * resolved, so that out of scope brands keep their current behaviour.
 */
export async function getTimeOutHandling(): Promise<TimeOutHandling> {
    try {
        const jurisdiction = await getUserJurisdiction();

        return getTimeOutHandlingForJurisdiction(jurisdiction);
    } catch (error) {
        console.error('Error resolving the user jurisdiction:', error);

        return TimeOutHandling.NONE;
    }
}
